import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  Col,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import { apiClient } from "../../services/api/apiClient";
import {
  STATUS_COLORS,
  STATUS_LABELS,
  diffBadge,
  fmtPct,
  fmtKg,
  fmtHa,
} from "./adminDashboardShared";

// `isoWeek` plugin para poder obtener el nº de semana ISO en el eje X
// de la progresión semanal — label "W<n>" en vez de "DD/MM".
dayjs.extend(isoWeek);

const { Text } = Typography;

interface AdminAuditsPayload {
  role: "admin";
  season: { id: number; label: string; year: number } | null;
  available_seasons: Array<{ id: number; label: string; year: number }>;
  kpis: {
    total: number;
    finalized: number;
    pending: number;
    pending_breakdown: Record<string, number>;
    canceled: number;
    prev_season: {
      total: number;
      finalized: number;
      year: number | null;
      label: string | null;
    };
  };
  by_state: Array<{ status: string; count: number }>;
  weekly_progression: Array<{
    week: string;
    planned_cum: number;
    finalized_cum: number;
  }>;
  by_company: Array<{
    company_id: number;
    company_name: string;
    total: number;
    finalized: number;
    pending: number;
    canceled: number;
  }>;
  by_auditor: Array<{
    auditor_id: number;
    auditor_name: string;
    company_name: string;
    total: number;
    finalized: number;
    pending: number;
    canceled: number;
    last_activity?: string | null;
    audited_kg?: number;
    audited_ha?: number;
  }>;
  by_province: Array<{
    province_name: string;
    count: number;
    finalized: number;
  }>;
}

interface Props {
  /** Campaña activa — null hasta que el wrapper carga `available_seasons`. */
  season: number | null;
}

/**
 * Panel admin · Auditorías. Scope `?scope=audits` del endpoint
 * `/api/v1/dashboard/`. Es "controlado" por el wrapper
 * `<AdminDashboard>`: éste pasa la campaña seleccionada vía prop para
 * que los tres paneles compartan el mismo selector.
 */
export const AdminAuditDashboard = ({ season }: Props) => {
  const [payload, setPayload] = useState<AdminAuditsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (season == null) return;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get<AdminAuditsPayload>(
          `/v1/dashboard/?scope=audits&season=${season}`,
        );
        setPayload(res.data);
      } catch (err: any) {
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            "No se pudo cargar el panel de auditorías",
        );
        setPayload(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [season]);

  const stateData = useMemo(
    () =>
      (payload?.by_state ?? []).map((r) => ({
        name: STATUS_LABELS[r.status] ?? r.status,
        value: r.count,
        color: STATUS_COLORS[r.status] ?? "#bfbfbf",
      })),
    [payload?.by_state],
  );

  const weeklyData = useMemo(
    () =>
      (payload?.weekly_progression ?? []).map((p) => ({
        // Label "W<n>" = nº de semana ISO. Si la campaña cruza año
        // natural (p.ej. Tango 2026 España = sep 2025 → sep 2026)
        // habrá dos W1, pero el eje temporal mantiene el orden.
        week: `W${dayjs(p.week).isoWeek()}`,
        Planificadas: p.planned_cum,
        Finalizadas: p.finalized_cum,
      })),
    [payload?.weekly_progression],
  );

  const companyData = useMemo(
    () =>
      (payload?.by_company ?? []).map((c) => {
        const pct =
          c.total > 0 ? Math.round((c.finalized / c.total) * 100) : 0;
        return {
          name: c.company_name,
          Finalizadas: c.finalized,
          Pendientes: c.pending,
          Canceladas: c.canceled,
          // Label al final de la barra (position="right" sobre el
          // último segmento del stack). Lo cargamos como campo
          // separado porque recharts' LabelList necesita `dataKey`
          // apuntando a un valor del datum.
          pctLabel: `${pct}% completado`,
        };
      }),
    [payload?.by_company],
  );

  // Opciones del dropdown-filter "Empresa" en la tabla de auditores:
  // listamos cada empresa distinta presente en la campaña actual.
  const auditorCompanyFilters = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ text: string; value: string }> = [];
    for (const row of payload?.by_auditor ?? []) {
      const c = row.company_name || "—";
      if (!seen.has(c)) {
        seen.add(c);
        out.push({ text: c, value: c });
      }
    }
    return out.sort((a, b) => a.text.localeCompare(b.text));
  }, [payload?.by_auditor]);

  if (error) return <Alert type="error" message={error} showIcon />;

  return (
    <Spin spinning={loading} tip="Cargando auditorías…">
      {payload && (
        <>
          {/* ── Fila 1 — KPIs ───────────────────────────────────── */}
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              {/* height: 100% + Row stretch (default) → KPIs mismos ancho+alto */}
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="Planificadas"
                  value={payload.kpis.total}
                  prefix={<TeamOutlined />}
                />
                <div style={{ marginTop: 6 }}>
                  {diffBadge(
                    payload.kpis.total,
                    payload.kpis.prev_season.total,
                  )}
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              {/* height: 100% + Row stretch (default) → KPIs mismos ancho+alto */}
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="Finalizadas"
                  value={payload.kpis.finalized}
                  valueStyle={{ color: STATUS_COLORS.finalized }}
                  prefix={<CheckCircleOutlined />}
                  suffix={
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      {fmtPct(payload.kpis.finalized, payload.kpis.total)}
                    </Text>
                  }
                />
                <Progress
                  percent={
                    payload.kpis.total > 0
                      ? Math.round(
                          (payload.kpis.finalized / payload.kpis.total) * 100,
                        )
                      : 0
                  }
                  strokeColor={STATUS_COLORS.finalized}
                  showInfo={false}
                  size="small"
                  style={{ marginTop: 6 }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              {/* height: 100% + Row stretch (default) → KPIs mismos ancho+alto */}
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="Pendientes"
                  value={payload.kpis.pending}
                  valueStyle={{ color: STATUS_COLORS.submitted }}
                  prefix={<ClockCircleOutlined />}
                />
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  {Object.entries(payload.kpis.pending_breakdown).map(
                    ([k, v]) =>
                      v > 0 ? (
                        <Tag
                          key={k}
                          color={STATUS_COLORS[k]}
                          style={{ marginBottom: 2 }}
                        >
                          {STATUS_LABELS[k] ?? k}: {v}
                        </Tag>
                      ) : null,
                  )}
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              {/* height: 100% + Row stretch (default) → KPIs mismos ancho+alto */}
              <Card style={{ height: "100%" }}>
                <Statistic
                  title="Canceladas"
                  value={payload.kpis.canceled}
                  valueStyle={{ color: STATUS_COLORS.canceled }}
                  prefix={<CloseCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* ── Fila 2 — Donut + Progresión temporal ─────────────── */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={10}>
              <Card title="Distribución por estado">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={stateData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      label={(entry: any) => `${entry.value}`}
                    >
                      {stateData.map((d, i) => (
                        <Cell key={i} fill={d.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
            <Col xs={24} md={14}>
              <Card title="Progresión semanal (acumulado)">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="Planificadas"
                      stroke={STATUS_COLORS.planned}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="Finalizadas"
                      stroke={STATUS_COLORS.finalized}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* ── Fila 3 — Empresas auditoras ──────────────────────── */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card title="Empresas auditoras">
                {companyData.length === 0 ? (
                  <Text type="secondary">
                    Sin datos de empresa auditora para esta campaña.
                  </Text>
                ) : (
                  <ResponsiveContainer
                    width="100%"
                    height={Math.max(120, companyData.length * 56)}
                  >
                    <BarChart
                      data={companyData}
                      layout="vertical"
                      // Más margen a la derecha para que el label
                      // "XX% completado" no quede cortado.
                      margin={{ left: 60, right: 110 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fontSize: 12 }}
                        width={180}
                      />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="Finalizadas"
                        stackId="a"
                        fill={STATUS_COLORS.finalized}
                      />
                      <Bar
                        dataKey="Pendientes"
                        stackId="a"
                        fill={STATUS_COLORS.submitted}
                      />
                      <Bar
                        dataKey="Canceladas"
                        stackId="a"
                        fill={STATUS_COLORS.canceled}
                      >
                        {/* position="right" sobre el ÚLTIMO segmento
                            del stack = fin de la barra total. Si
                            canceladas = 0 el segmento tiene 0 ancho
                            pero su right edge sigue siendo el total. */}
                        <LabelList
                          dataKey="pctLabel"
                          position="right"
                          style={{
                            fontSize: 11,
                            fill: STATUS_COLORS.finalized,
                            fontWeight: 500,
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Card>
            </Col>
          </Row>

          {/* ── Fila 4 — Ranking auditores ───────────────────────── */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card title="Ranking de auditores">
                <Table
                  size="small"
                  rowKey="auditor_id"
                  dataSource={payload.by_auditor}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  scroll={{ x: 900 }}
                  columns={[
                    {
                      title: "Auditor",
                      dataIndex: "auditor_name",
                      key: "auditor_name",
                      sorter: (a, b) =>
                        a.auditor_name.localeCompare(b.auditor_name),
                      filters: auditorCompanyFilters,
                      filterSearch: true,
                      // El filtro cae sobre `company_name` (dropdown
                      // de empresas), mientras que lo que se muestra
                      // en la celda es el nombre del auditor + tag
                      // con la empresa debajo — así nos ahorramos la
                      // columna "Empresa" dedicada.
                      onFilter: (value, record) =>
                        (record.company_name || "—") === value,
                      render: (name: string, r) => (
                        <div style={{ lineHeight: 1.2 }}>
                          <div>{name}</div>
                          <Tag
                            style={{ marginTop: 4, marginRight: 0, fontSize: 11 }}
                            color="blue"
                          >
                            {r.company_name || "—"}
                          </Tag>
                        </div>
                      ),
                    },
                    {
                      title: "Audits",
                      dataIndex: "total",
                      key: "total",
                      width: 90,
                      align: "right",
                      sorter: (a, b) => a.total - b.total,
                      defaultSortOrder: "descend",
                    },
                    {
                      title: "Finalizadas",
                      dataIndex: "finalized",
                      key: "finalized",
                      width: 110,
                      align: "right",
                      sorter: (a, b) => a.finalized - b.finalized,
                    },
                    {
                      title: "Pendientes",
                      dataIndex: "pending",
                      key: "pending",
                      width: 110,
                      align: "right",
                      sorter: (a, b) => a.pending - b.pending,
                    },
                    {
                      title: "Kg auditados",
                      dataIndex: "audited_kg",
                      key: "audited_kg",
                      width: 130,
                      align: "right",
                      sorter: (a, b) =>
                        (a.audited_kg ?? 0) - (b.audited_kg ?? 0),
                      render: (v: number | undefined) => fmtKg(v ?? 0),
                    },
                    {
                      title: "Ha auditadas",
                      dataIndex: "audited_ha",
                      key: "audited_ha",
                      width: 120,
                      align: "right",
                      sorter: (a, b) =>
                        (a.audited_ha ?? 0) - (b.audited_ha ?? 0),
                      render: (v: number | undefined) => fmtHa(v ?? 0),
                    },
                    {
                      // Productividad por hectárea — kg aforados / ha
                      // auditada. Permite comparar auditores que
                      // trabajan parcelas pequeñas de alto rendimiento
                      // vs los que cubren mucha superficie poco
                      // productiva. Si ha=0 ocultamos (dividir por 0).
                      title: "Kg/ha",
                      key: "kg_per_ha",
                      width: 120,
                      align: "right",
                      sorter: (a, b) => {
                        const ra =
                          a.audited_ha && a.audited_ha > 0
                            ? (a.audited_kg ?? 0) / a.audited_ha
                            : 0;
                        const rb =
                          b.audited_ha && b.audited_ha > 0
                            ? (b.audited_kg ?? 0) / b.audited_ha
                            : 0;
                        return ra - rb;
                      },
                      render: (_: any, r) => {
                        if (!r.audited_ha || r.audited_ha === 0) return "—";
                        const v =
                          (r.audited_kg ?? 0) / (r.audited_ha ?? 1);
                        return `${Math.round(v).toLocaleString("es-ES")} kg/ha`;
                      },
                    },
                    {
                      title: "% completado",
                      key: "pct",
                      width: 160,
                      align: "right",
                      sorter: (a, b) =>
                        (a.total > 0 ? a.finalized / a.total : 0) -
                        (b.total > 0 ? b.finalized / b.total : 0),
                      render: (_: any, r) => {
                        const pct =
                          r.total > 0
                            ? Math.round((r.finalized / r.total) * 100)
                            : 0;
                        return (
                          <Progress
                            percent={pct}
                            size="small"
                            strokeColor={STATUS_COLORS.finalized}
                            style={{ margin: 0 }}
                          />
                        );
                      },
                    },
                    {
                      title: "Última actividad",
                      dataIndex: "last_activity",
                      key: "last_activity",
                      width: 140,
                      render: (v) =>
                        v ? dayjs(v).format("DD/MM/YYYY") : "—",
                    },
                  ]}
                  // Fila de totales al pie: suma los valores de los
                  // registros VISIBLES (post-filtrado), para que al
                  // filtrar por una empresa concreta los totales se
                  // adapten. AntD pasa el array filtrado como `rows`.
                  summary={(rows) => {
                    const t = (rows as any[]).reduce(
                      (acc, r) => ({
                        total: acc.total + (r.total ?? 0),
                        finalized: acc.finalized + (r.finalized ?? 0),
                        pending: acc.pending + (r.pending ?? 0),
                        audited_kg: acc.audited_kg + (r.audited_kg ?? 0),
                        audited_ha: acc.audited_ha + (r.audited_ha ?? 0),
                      }),
                      {
                        total: 0,
                        finalized: 0,
                        pending: 0,
                        audited_kg: 0,
                        audited_ha: 0,
                      },
                    );
                    const pct =
                      t.total > 0
                        ? Math.round((t.finalized / t.total) * 100)
                        : 0;
                    const kgha =
                      t.audited_ha > 0 ? t.audited_kg / t.audited_ha : 0;
                    const bold = { fontWeight: 600 };
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            <span style={bold}>Total</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <span style={bold}>{t.total}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">
                            <span style={bold}>{t.finalized}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="right">
                            <span style={bold}>{t.pending}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right">
                            <span style={bold}>{fmtKg(t.audited_kg)}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="right">
                            <span style={bold}>{fmtHa(t.audited_ha)}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={6} align="right">
                            <span style={bold}>
                              {kgha
                                ? `${Math.round(kgha).toLocaleString("es-ES")} kg/ha`
                                : "—"}
                            </span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={7} align="right">
                            <span style={bold}>{pct}%</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={8}>
                            <span style={{ color: "#bfbfbf" }}>—</span>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              </Card>
            </Col>
          </Row>

          {/* ── Fila 5 — Reparto por provincia ───────────────────── */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card title="Top 10 provincias · total vs finalizadas">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={payload.by_province.map((p) => ({
                      name: p.province_name,
                      Total: p.count,
                      Finalizadas: p.finalized,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11 }}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Total" fill={STATUS_COLORS.planned} />
                    <Bar
                      dataKey="Finalizadas"
                      fill={STATUS_COLORS.finalized}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Spin>
  );
};

/* `Space` sigue siendo necesario como import en el ecosistema del
 * repo; lo mantenemos aquí aunque este panel no renderice uno. */
void Space;
